import React, { useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { DataService } from "../../services/dataService";
import type { IDokumentItem } from "../../models/IDokumentItem";
import type { IKachelmatrixItem } from "../../models/IKachelmatrixItem";
import type { IProjektItem } from "../../models/IProjektItem";
import {
  AddRegular,
  Dismiss24Regular,
  DismissCircleFilled,
  EditRegular,
  MoreHorizontalRegular,
} from "@fluentui/react-icons";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Drawer,
  DrawerBody,
  DrawerHeaderTitle,
  Input,
  Label,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Textarea,
  Text,
  Dropdown,
  Option,
} from "@fluentui/react-components";
import Dokumente from "./dokumente";
import { useProjektplanStyles as useStyles } from "../../utils/styles";
import { CheckmarkFilled } from "@fluentui/react-icons";
import type { IWorkflowItem } from "../../models/IWorkflowItem";

interface KachelmatrixProps {
  canEdit?: boolean;
  projekt: IProjektItem;
  dokumente: IDokumentItem[];
  onProjektUpdated: () => Promise<void>;
  onDokumenteUpdated: () => Promise<void>;
}
type LoadingState = "anfordern" | "angefordert" | "genehmigt" | "abgelehnt";

const Kachelmatrix: React.FC<KachelmatrixProps> = ({
  canEdit = false,
  projekt,
  dokumente,
  onProjektUpdated,
  onDokumenteUpdated,
}) => {
  const panelTopRef = useRef<HTMLDivElement>(null);
  const styles = useStyles();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [color, setColor] = useState("");

  const [module, setModule] = useState<IKachelmatrixItem[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [updateBemerkung, setUpdateBemerkung] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerItem, setDrawerItem] = useState<IKachelmatrixItem | null>(null);

  const pastelColorOptions = [
    { label: "Rot", value: "#f8d7da" },
    { label: "Gelb", value: "#fef3c7" },
    { label: "Blau", value: "#dbeafe" },
    { label: "Orange", value: "#fde7c8" },
    { label: "Grün", value: "#ace3b3" },
    { label: "Violett", value: "#e9d5ff" },
  ];

  const [loadingState, setLoadingState] = React.useState<LoadingState>(
    projekt.workflowStatusKachelmatrix == "Nicht versendet"
      ? "anfordern"
      : projekt.workflowStatusKachelmatrix == "Angefordert"
        ? "angefordert"
        : projekt.workflowStatusKachelmatrix == "Genehmigt"
          ? "genehmigt"
          : "abgelehnt",
  );

  const scrollToTopOfPanel = () => {
    setTimeout(() => {
      if (panelTopRef.current) {
        // First, use scrollIntoView immediately
        panelTopRef.current.scrollIntoView({
          behavior: "auto",
          block: "start",
        });

        // Then find and scroll the scrollable parent to ensure we're at the top
        let element = panelTopRef.current.parentElement;
        while (element) {
          const overflowY = window.getComputedStyle(element).overflowY;
          const isScrollable = overflowY === "scroll" || overflowY === "auto";
          if (isScrollable && element.scrollHeight > element.clientHeight) {
            element.scrollTop = 0;
            break;
          }
          element = element.parentElement;
        }
      }
    }, 150);
  };

  const buttonIcon =
    loadingState === "angefordert" ? (
      <Spinner size="tiny" />
    ) : loadingState === "genehmigt" ? (
      <CheckmarkFilled />
    ) : loadingState === "abgelehnt" ? (
      <DismissCircleFilled />
    ) : null;

  React.useEffect(() => {
    const fetchItems = async () => {
      if (!dataService) return;
      const response = await dataService.getModuleKachelmatrix(
        projekt.itemId,
        "1",
      );
      if (response) {
        setModule(response);
      }
    };
    fetchItems();
  }, [dataService, projekt.itemId]);

  const semesterGroups = React.useMemo(() => {
    const groups = new Map<number, IKachelmatrixItem[]>();

    module.forEach((item) => {
      const semester = item.semester || 0;
      const existing = groups.get(semester) ?? [];
      groups.set(semester, [...existing, item]);
    });

    return Array.from(groups.entries())
      .sort(([semesterA], [semesterB]) => semesterA - semesterB)
      .map(([semester, items]) => ({
        semester,
        items: [...items].sort((a, b) =>
          a.bezeichnung.localeCompare(b.bezeichnung),
        ),
        totalEcts: items.reduce((sum, item) => sum + (item.ects ?? 0), 0),
      }));
  }, [module]);

  const handleDeleteKachelmatrix = async (modulId: number) => {
    if (!dataService) return;

    try {
      await dataService.deleteKachelmatrix(modulId);
      setModule((current) => current.filter((item) => item.Id !== modulId));
    } catch (error) {
      console.error("Failed to delete module entry:", error);
      setErrorMessage("Das Modul konnte nicht gelöscht werden.");
      setColor("red");
      scrollToTopOfPanel();
    }
  };

  const startEditModule = (item: IKachelmatrixItem | undefined) => {
    if (!item) {
      item = {
        Id: 0,
        projektId: projekt.itemId,
        version: "1",
        semester: 0,
        ects: 0,
        bezeichnung: "",
        farbe: "",
        sortierung: 0,
      };
    }
    setDrawerItem({
      ...item,
      projektId: item.projektId || projekt.itemId,
      version: item.version || "1",
    });
    setIsDrawerOpen(true);
  };

  const updateDrawerItem = <K extends keyof IKachelmatrixItem>(
    field: K,
    value: IKachelmatrixItem[K],
  ) => {
    setDrawerItem((current) => {
      if (!current) return current;
      return {
        ...current,
        [field]: value,
      };
    });
  };

  const saveDrawerItem = async () => {
    if (!drawerItem || !dataService) {
      setErrorMessage("Das Modul konnte nicht gespeichert werden.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }

    if (!drawerItem.bezeichnung.trim() || drawerItem.ects <= 0) {
      setErrorMessage("Bitte geben Sie eine Bezeichnung und gültige ECTS ein.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }

    setIsSaving(true);
    setErrorMessage("");

    try {
      const payload: IKachelmatrixItem = {
        ...drawerItem,
        bezeichnung: drawerItem.bezeichnung.trim(),
        projektId: projekt.itemId,
        version: drawerItem.version || "1",
      };

      const result = await dataService.updateKachelmatrixItem(payload);
      if (result === false) {
        throw new Error("Module update was not successful.");
      }

      setModule((current) =>
        current.map((item) => (item.Id === payload.Id ? payload : item)),
      );
      setIsDrawerOpen(false);
      setDrawerItem(null);
      setErrorMessage("Modul erfolgreich gespeichert.");
      setColor("green");
      scrollToTopOfPanel();
    } catch (error) {
      console.error("Failed to save module:", error);
      setErrorMessage(
        "Fehler beim Speichern des Moduls. Bitte erneut versuchen.",
      );
      setColor("red");
      scrollToTopOfPanel();
    } finally {
      setIsSaving(false);
    }
  };

  const saveProjekt = async () => {
    if (isSaving) return;
    setErrorMessage("");
    if (!canEdit || !dataService) {
      setErrorMessage("Die Daten können derzeit nicht gespeichert werden.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }
    setIsSaving(true);
    try {
      const result = await dataService.addUpdateProjekt({
        ...projekt,

        updateBemerkung: updateBemerkung,
        kategorie: "Projektplan",
      });
      if (result === false) {
        throw new Error("Project update was not successful.");
      }

      await onProjektUpdated();
      setUpdateBemerkung("");
      setErrorMessage("Daten erfolgreich gespeichert");
      setColor("green");
      scrollToTopOfPanel();
      setTimeout(() => {
        setErrorMessage("");
        setEditMode(false);
        setIsSaving(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to save project or competencies:", error);
      setErrorMessage(
        "Fehler beim Speichern der Projektdaten oder Meilensteine. Bereits gespeicherte Änderungen bleiben erhalten. Bitte erneut versuchen.",
      );
      setColor("red");
      scrollToTopOfPanel();
      setIsSaving(false);
    }
  };

  const _saveClicked = () => {
    setIsDialogOpen(true);
  };

  const _startWorkflow = async () => {
    if (isSaving) return;
    setErrorMessage("");
    if (!canEdit || !dataService) {
      setErrorMessage("Die Daten können derzeit nicht gespeichert werden.");
      setColor("red");
      scrollToTopOfPanel();
      return;
    }
    setIsSaving(true);
    try {
      const item: IWorkflowItem = {
        projektId: projekt.itemId,
        source: "Kachelmatrix",
        genehmigt: false,
        abgelehnt: false,
        id: 0,
        zugewiesen: "",
        titel: "",
        link: "",
        beschreibung: "",
        taskGuid: "",
        createdBy: "",
        created: new Date(),
        kommentar: "",
      };
      const response = await dataService.startWorkflow(item);
      if (response === false) {
        throw new Error(
          `Workflow start was not successful for project ${item.projektId}.`,
        );
      }
      await onProjektUpdated();
      setErrorMessage("Workflow erfolgreich gestartet");
      setColor("green");
      scrollToTopOfPanel();
      setTimeout(() => {
        setErrorMessage("");
        setIsSaving(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to start workflow:", error);
      setErrorMessage(
        "Fehler beim Starten des Workflows. Bitte erneut versuchen.",
      );
      setColor("red");
      setIsSaving(false);
      scrollToTopOfPanel();
    }
  };

  const renderReadMode = () => (
    <>
      <div className="verticalLayout">
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Kachelmatrix</h3>
            {canEdit && (
              <Button
                appearance="secondary"
                icon={<EditRegular />}
                onClick={() => {
                  setEditMode(true);
                }}
              >
                Bearbeitungsmodus starten
              </Button>
            )}
          </div>
          <div>
            <div
              style={{
                marginBottom: "8px",
                fontWeight: "bolder",
                color:
                  projekt.workflowStatusKachelmatrix === "Abgelehnt"
                    ? "#b33a34"
                    : projekt.workflowStatusKachelmatrix === "Genehmigt"
                      ? "#408a3e"
                      : projekt.workflowStatusKachelmatrix === "Angefordert"
                        ? "#d69d60"
                        : "#608bd6",
              }}
            >
              {projekt.workflowKachelmatrix}
            </div>
            {semesterGroups.length > 0 ? (
              renderModuleGrid()
            ) : (
              <div
                style={{
                  padding: "16px",
                  borderRadius: "12px",
                  background: "#f5f7fb",
                  color: "#5d697d",
                }}
              >
                Keine Module für die Kachelmatrix vorhanden.
              </div>
            )}
          </div>
        </section>

        <Dokumente projektId={projekt.itemId} dokumente={dokumente} />
      </div>
    </>
  );

  const renderModuleGrid = () => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${Math.max(1, semesterGroups.length)}, minmax(180px, 1fr))`,
        gap: "16px",
        alignItems: "start",
        width: "100%",
      }}
    >
      {semesterGroups.map(({ semester, items, totalEcts }) => (
        <div
          key={semester}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            minWidth: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
              textAlign: "center",
              color: "#3d4968",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "0.04em",
              }}
            >
              Semester {semester}
            </div>
            <div style={{ fontSize: "12px", fontWeight: 600, opacity: 0.8 }}>
              {totalEcts} ECTS
            </div>
          </div>

          {items.map((item) => {
            const height = Math.max(36, (item.ects ?? 1) * 2 * 18);

            return (
              <div
                key={item.Id}
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: `${height}px`,
                  height: `${height}px`,
                  borderRadius: "12px",
                  background: item.farbe || "#e6f0f5",
                  border: "1px solid rgba(96, 139, 214, 0.18)",
                  boxShadow: "0 1px 2px rgba(25, 40, 60, 0.05)",
                  padding: "10px 12px 12px",
                  color: "#1d2b3a",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      fontSize: "14px",
                      fontWeight: 600,
                      lineHeight: 1.35,
                      overflow: "hidden",
                      wordBreak: "break-word",
                    }}
                  >
                    {item.bezeichnung}
                    <br />
                    ECTS: {item.ects}
                  </div>
                  {editMode && (
                    <Menu positioning="below-end">
                      <MenuTrigger disableButtonEnhancement>
                        <Button
                          appearance="subtle"
                          size="small"
                          aria-label={`Open actions for ${item.bezeichnung}`}
                          icon={<MoreHorizontalRegular />}
                          style={{
                            minWidth: "32px",
                            width: "32px",
                            height: "32px",
                            padding: 0,
                            borderRadius: "8px",
                            background: "rgba(255, 255, 255, 0.15)",
                          }}
                        />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem onClick={() => startEditModule(item)}>
                            Modul bearbeiten
                          </MenuItem>
                          <MenuItem
                            onClick={() =>
                              void handleDeleteKachelmatrix(item.Id)
                            }
                          >
                            Modul löschen
                          </MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  const renderEditMode = () => (
    <>
      <div ref={panelTopRef}>
        {errorMessage && (
          <div
            style={{
              fontSize: "16px",
              marginBottom: "30px",
              maxWidth: "500px",
              color: color,
            }}
          >
            {errorMessage}
          </div>
        )}
      </div>
      <div className="verticalLayout">
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>Kachelmatrix</h3>
            <div style={{ display: "flex", gap: "8px" }}>
              {canEdit && (
                <>
                  <Button
                    appearance="primary"
                    disabled={isSaving}
                    icon={<AddRegular />}
                    onClick={() => {
                      startEditModule(undefined);
                    }}
                  >
                    Neues Modul
                  </Button>

                  <Button
                    appearance="primary"
                    disabled={isSaving}
                    onClick={() => {
                      _saveClicked();
                    }}
                  >
                    Speichern
                  </Button>
                </>
              )}
              <Button
                appearance="secondary"
                onClick={() => {
                  setErrorMessage("");
                  setUpdateBemerkung("");
                  setEditMode(false);
                }}
              >
                Abbrechen
              </Button>
            </div>
          </div>
          <div>
            <div className={styles.gridContainer}>
              {canEdit && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    marginBottom: "8px",
                    paddingBottom: 0,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontWeight: "bolder",
                      fontSize: "14px",
                      color:
                        projekt.workflowKachelmatrix === "Abgelehnt"
                          ? "#b33a34"
                          : projekt.workflowKachelmatrix === "Genehmigt"
                            ? "#408a3e"
                            : projekt.workflowKachelmatrix === "Angefordert"
                              ? "#d69d60"
                              : "#608bd6",
                    }}
                  >
                    {projekt.workflowKachelmatrix}
                  </span>

                  <Button
                    appearance="primary"
                    disabled={isSaving || loadingState !== "anfordern"}
                    onClick={() => {
                      _startWorkflow();
                      setLoadingState("angefordert");
                    }}
                    style={{
                      backgroundColor:
                        loadingState === "abgelehnt" ? "firebrick" : undefined,
                    }}
                    icon={buttonIcon}
                  >
                    Freigabe {loadingState}
                  </Button>
                </div>
              )}
            </div>
            {semesterGroups.length > 0 ? (
              renderModuleGrid()
            ) : (
              <div
                style={{
                  padding: "16px",
                  borderRadius: "12px",
                  background: "#f5f7fb",
                  color: "#5d697d",
                }}
              >
                Keine Module für die Kachelmatrix vorhanden.
              </div>
            )}
          </div>
        </section>

        <Dokumente
          projektId={projekt.itemId}
          dokumente={dokumente}
          editable={canEdit}
          onDokumenteUpdated={onDokumenteUpdated}
        />
      </div>
      <Dialog
        open={isDialogOpen}
        onOpenChange={(_e, data) => setIsDialogOpen(data.open)}
      >
        <DialogSurface>
          <DialogTitle style={{ marginBottom: "10px" }}>
            Änderungsbegründung
          </DialogTitle>
          <DialogContent>
            <div className={styles.label}>Begründung der Änderung:</div>
            <div className={styles.value}>
              <Textarea
                className={styles.input}
                id="bemerkung"
                value={updateBemerkung}
                autoFocus
                rows={3}
                onChange={(e) => setUpdateBemerkung(e.target.value)}
              />
            </div>
          </DialogContent>
          <DialogActions style={{ marginTop: "10px" }}>
            <Button
              appearance="primary"
              disabled={!updateBemerkung || isSaving}
              onClick={() => {
                setIsDialogOpen(false);
                void saveProjekt();
              }}
            >
              Speichern
            </Button>
            <Button
              appearance="secondary"
              onClick={() => setIsDialogOpen(false)}
            >
              Abbrechen
            </Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </>
  );

  return editMode ? (
    <>
      {renderEditMode()}
      <Drawer
        size={"medium"}
        open={isDrawerOpen}
        onOpenChange={(_, { open }) => {
          if (!open) {
            setIsDrawerOpen(false);
            setDrawerItem(null);
          } else {
            setIsDrawerOpen(true);
          }
        }}
        position="end"
      >
        <DrawerHeaderTitle
          style={{ paddingLeft: "15px", paddingTop: "15px" }}
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={() => {
                setIsDrawerOpen(false);
                setDrawerItem(null);
              }}
            />
          }
        >
          <Text style={{ fontSize: "1.5rem", fontWeight: 600 }}>
            Modul bearbeiten
          </Text>
        </DrawerHeaderTitle>
        <DrawerBody>
          <div ref={panelTopRef} style={{ padding: "0 20px 20px" }}>
            {errorMessage && (
              <div
                style={{
                  fontSize: "16px",
                  marginBottom: "20px",
                  color: color,
                }}
              >
                {errorMessage}
              </div>
            )}

            {drawerItem && (
              <div className={styles.page}>
                <div className={styles.sidePanelSection}>
                  <div className={styles.formGrid}>
                    <div className={styles.field}>
                      <Label
                        required
                        className={styles.sidePanelLabel}
                        style={{ paddingTop: "0px" }}
                      >
                        Semester
                      </Label>
                      <Dropdown
                        className={styles.input}
                        id="Semester"
                        value={String(drawerItem.semester ?? 0)}
                        onOptionSelect={(_e, option) =>
                          option &&
                          updateDrawerItem(
                            "semester",
                            Number(option.optionValue),
                          )
                        }
                      >
                        <Option value="0">0</Option>
                        <Option value="1">1</Option>
                        <Option value="2">2</Option>
                        <Option value="3">3</Option>
                        <Option value="4">4</Option>
                        <Option value="5">5</Option>
                        <Option value="6">6</Option>
                      </Dropdown>
                    </div>
                    <div className={styles.field}>
                      <Label
                        required
                        className={styles.sidePanelLabel}
                        style={{ paddingTop: "0px" }}
                      >
                        ECTS
                      </Label>
                      <Input
                        className={styles.input}
                        placeholder="Bitte ECTS eingeben. (3, 5, ...)"
                        type="number"
                        value={String(drawerItem.ects ?? 0)}
                        onChange={(_, data) =>
                          updateDrawerItem("ects", Number(data.value || 0))
                        }
                      />
                    </div>
                  </div>
                  <Label
                    required
                    className={styles.sidePanelLabel}
                    style={{ paddingTop: "0px" }}
                  >
                    Modulname
                  </Label>
                  <Input
                    className={styles.input}
                    placeholder="Bitte Modulname eingeben"
                    value={drawerItem.bezeichnung}
                    onChange={(_, data) =>
                      updateDrawerItem("bezeichnung", data.value)
                    }
                  />

                  <Label
                    required
                    className={styles.sidePanelLabel}
                    style={{ paddingTop: "10px" }}
                  >
                    Farbe
                  </Label>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      paddingTop: "6px",
                      paddingBottom: "15px",
                    }}
                  >
                    {pastelColorOptions.map((colorOption) => (
                      <button
                        key={colorOption.label}
                        type="button"
                        aria-label={colorOption.label}
                        onClick={() =>
                          updateDrawerItem("farbe", colorOption.value)
                        }
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "8px",
                          border:
                            drawerItem.farbe === colorOption.value
                              ? "2px solid #1f2937"
                              : "1px solid rgba(31, 41, 55, 0.2)",
                          background: colorOption.value,
                          cursor: "pointer",
                          boxShadow:
                            drawerItem.farbe === colorOption.value
                              ? "0 0 0 2px rgba(31, 41, 55, 0.12)"
                              : "none",
                        }}
                      />
                    ))}
                  </div>

                  <div className={styles.footer}>
                    <Button
                      appearance="primary"
                      disabled={isSaving}
                      onClick={() => void saveDrawerItem()}
                    >
                      Speichern
                    </Button>
                    <Button
                      appearance="secondary"
                      onClick={() => {
                        setIsDrawerOpen(false);
                        setDrawerItem(null);
                        setErrorMessage("");
                      }}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DrawerBody>
      </Drawer>
    </>
  ) : (
    <>{renderReadMode()}</>
  );
};

export default Kachelmatrix;
